// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuidv4 } from 'uuid';

import type { DocNode } from '@atlaskit/adf-schema';
import { getEmptyADF } from '@atlaskit/adf-utils/empty-adf';
import type { Slice } from '@atlaskit/editor-prosemirror/model';
import type { Fragment, Schema } from '@atlaskit/editor-prosemirror/model';
import type { ADFStreamer } from '@atlassian/ai-model-io/adf-streamer';
import { getXChannelIdHeader, getXProductHeader } from '@atlassian/editor-ai-common/api/request';
import type { StreamFlowPacer } from '@atlassian/editor-ai-common/api/stream-flow-pacer';

import type {
	ConvoAIResponseRovoAction,
	EditorPluginAIPromptResponseMeta,
	EditorPluginAIProvider,
} from '../../../types/types';
import type { SelectionType } from '../../prebuilt/config-items/config-items';
import { getAPIName } from '../../prebuilt/utils';
import { correctADFCapitalization } from '../adf-capitalization-correction';
import type { FireAIAnalyticsEventCallback } from '../analytics/utils';
import { FAILURE_REASON } from '../errors';
import { getSelectionCategory } from '../selection';

import { createADFStreamer } from './adf-streaming';
import { parseAdfToMarkdown } from './parse-adf-to-markdown';
import { ADFStreamingRetryManager } from './retry-manager';
import type { AIExperienceMachineContextForRequest, RequestOptions, ResponseObject } from './types';

type Callback = (event: ResponseObject) => void;
type StreamContentProcessor = (streamContent: string) => { fragment?: Fragment; markdown: string };
type IsComplete = () => boolean;
type SteamRequestOptions = Extract<RequestOptions, { payload: string }>;
type StreamHandlerProps = {
	// doc?: string;
	adfStreamer?: ADFStreamer;
	cacheKey: string;
	context: AIExperienceMachineContextForRequest;
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	data: any;
	isAdfPrompt: boolean;
	isComplete?: IsComplete;
	// selection?: string;
	partial: LinePartial;
	requestOptions: SteamRequestOptions;
	schema: Schema;
	selectionType: SelectionType;
	streamContentProcessor?: StreamContentProcessor;
};

const ADF_STREAMING_PROCESS_INTERVAL = 250; // 4 re-renders per second
const ADF_STREAMING_PROCESS_INITIAL_CHARS_PER_TICK = 5000;
const ADF_STREAMING_PROCESS_MAX_CHARS_PER_TICK = 10000;
const ADF_STREAMING_RETRY_MAX_RETRIES = 1;
const ADF_STREAMING_RETRY_DELAY_MS = 10000;
let canProcessADFStreaming = false;
let adfStreamingProcessTimer: ReturnType<typeof setInterval> | null = null;
function startADFStreamingProcessTimer() {
	stopADFStreamingProcessTimer();
	adfStreamingProcessTimer = setInterval(() => {
		canProcessADFStreaming = true;
	}, ADF_STREAMING_PROCESS_INTERVAL);
}

function stopADFStreamingProcessTimer() {
	if (adfStreamingProcessTimer) {
		clearInterval(adfStreamingProcessTimer);
		adfStreamingProcessTimer = null;
	}
}

function getInputSlice(context: AIExperienceMachineContextForRequest): Slice | undefined {
	const { editorView, positions } = context;
	if (!positions || positions.length < 2) {
		return undefined;
	}
	const from = Number(positions[0]);
	const to = Number(positions[1]);
	if (!Number.isFinite(from) || !Number.isFinite(to)) {
		return undefined;
	}
	try {
		return editorView.state.doc.slice(from, to);
	} catch {
		return undefined;
	}
}

function parsedHasExpectedKey<Key extends string>(
	key: Key,
	parsed: unknown,
): parsed is { [key in Key]: string } {
	if (typeof parsed !== 'object' || parsed === null || !(key in parsed)) {
		return false;
	}

	return true;
}

function sanitizeErrorContent(error: unknown): string {
	if (typeof error === 'string') {
		return error;
	}

	if (error instanceof Error) {
		return error.message;
	}

	return `Unknown error: ${error === null ? 'null' : error?.toString()}`;
}

function handleRequestError(error: unknown, endpoint: string): ResponseObject {
	const isErrorUnknown =
		(parsedHasExpectedKey('name', error) && error.name === 'AbortError') ||
		(parsedHasExpectedKey('cause', error) && error.cause === 'ConvoAIChannelCreationError');

	// This can be due to code crashes too, but falls back to network errors
	if (!isErrorUnknown) {
		// eslint-disable-next-line no-console
		console.error('Editor AI: startStreamingResponse()', error);
	}

	return {
		type: 'error',
		errorInfo: {
			apiName: getAPIName(endpoint),
			failureReason: FAILURE_REASON.API_FAIL,
			statusCode: parsedHasExpectedKey('status', error) ? Number(error.status) : 9000,
			errorContent: sanitizeErrorContent(error),
		},
	};
}

// For non 200 status codes
// See details https://hello.atlassian.net/wiki/spaces/CA3/pages/2485466879/Generative+AI+API+Specs#Error-codes
async function handleResponseNotOK({
	requestOptions,
	cacheKey,
	response,
}: {
	cacheKey: string;
	doc?: string;
	requestOptions: SteamRequestOptions;
	response: Response;
	selection?: string;
}): Promise<ResponseObject> {
	if (response.status === 400) {
		try {
			// Errors with special handling
			// We have soft alignment with the BE that for inputs that are not suitable
			// for prompts -- we want to rely on openai providing a suitable message for
			// the user. And not have manually maintained guards/user feedback messages.
			// https://product-fabric.atlassian.net/wiki/spaces/EUXQ/pages/3573548013/Introducing+AI+Dev+Documentation+TODO+AIFOLLOWUP+audit
			const knownErrors = [
				'INPUT_TOO_SHORT_TO_SUMMARIZE',
				'INPUT_TOO_SHORT_TO_PROCESS',
				'INPUT_EXCEEDS_TOKEN_LIMIT',
			] as const;
			const { error } = await response.json();
			if (error) {
				if (typeof error === 'object' && 'key' in error && knownErrors.includes(error.key)) {
					if (error.key === 'INPUT_EXCEEDS_TOKEN_LIMIT') {
						return {
							type: 'error',
							errorInfo: {
								apiName: getAPIName(requestOptions.endpoint),
								failureReason: FAILURE_REASON.TOKEN_LIMIT,
								statusCode: response.status,
								errorKey: error.key,
							},
						};
					}
					if (
						error.key === 'INPUT_TOO_SHORT_TO_SUMMARIZE' ||
						error.key === 'INPUT_TOO_SHORT_TO_PROCESS'
					) {
						return {
							type: 'error',
							errorInfo: {
								apiName: getAPIName(requestOptions.endpoint),
								failureReason: FAILURE_REASON.INPUT_TOO_SHORT,
								statusCode: response.status,
								errorKey: error.key,
							},
						};
					}
				}
				return {
					type: 'error',
					errorInfo: {
						apiName: getAPIName(requestOptions.endpoint),
						failureReason: FAILURE_REASON.API_FAIL,
						statusCode: response.status,
						errorContent: 'Unhandled error response received',
					},
				};
			}
		} catch (backendError) {
			return {
				type: 'error',
				errorInfo: {
					apiName: getAPIName(requestOptions.endpoint),
					failureReason: FAILURE_REASON.API_FAIL,
					statusCode: response.status,
					errorContent: sanitizeErrorContent(backendError),
				},
			};
		}
	}

	// The BE are unable to add the header due to constraints with the service proxy
	// There is a ticket to add this via feature request
	// https://hello.jira.atlassian.cloud/browse/SPSVC-28
	// For now we are just hard coding the value
	if (response.status === 429) {
		const retryAfter = response.headers.get('Retry-After') || '60';
		return {
			type: 'error',
			retryAfter: Number(retryAfter),
			errorInfo: {
				apiName: getAPIName(requestOptions.endpoint),
				failureReason: FAILURE_REASON.RATE_LIMITED,
				statusCode: response.status,
			},
		};
	}

	// This status code is being used to indicate that the request was unable to be handled
	// due to the content not meeting the Acceptable Use Policy.
	if (response.status === 451) {
		return {
			type: 'error',
			cacheKey,
			modelInput: { selection: requestOptions.selection, doc: requestOptions.doc },
			errorInfo: {
				apiName: getAPIName(requestOptions.endpoint),
				failureReason: FAILURE_REASON.AUP_VIOLATION,
				statusCode: 451,
			},
		};
	}

	if (response.status === 9001) {
		return {
			type: 'error',
			errorInfo: {
				apiName: getAPIName(requestOptions.endpoint),
				failureReason: FAILURE_REASON.ELEVATE_DISABLED_GENERATE,
				statusCode: 9001,
			},
		};
	}

	return {
		type: 'error',
		errorInfo: {
			apiName: getAPIName(requestOptions.endpoint),
			failureReason: FAILURE_REASON.API_FAIL,
			statusCode: response.status,
			errorContent: `unexpected response status: ${response.status}`,
		},
	};
}

function handleNoResponseBody({
	response,
	requestOptions,
}: {
	requestOptions: SteamRequestOptions;
	response: Response;
}): ResponseObject {
	return {
		type: 'error',
		errorInfo: {
			apiName: getAPIName(requestOptions.endpoint),
			failureReason: FAILURE_REASON.API_FAIL,
			statusCode: response.status,
			errorContent: 'response.body missing',
		},
	};
}

function handleLineNoGeneratedContent({
	requestOptions,
	cacheKey,
	data,
}: StreamHandlerProps): ResponseObject {
	const { selection, doc } = requestOptions;
	if (data.error?.statusCode === 429) {
		return {
			type: 'error',
			retryAfter: 60,
			errorInfo: {
				apiName: getAPIName(requestOptions.endpoint),
				failureReason: FAILURE_REASON.RATE_LIMITED,
				statusCode: 429,
			},
		};
	}

	if (data.error?.key === 'RESPONSE_TOO_SIMILAR') {
		const inputOutputDiffRatio =
			parsedHasExpectedKey('meta', data) && parsedHasExpectedKey('inputOutputDiffRatio', data.meta)
				? data['meta']['inputOutputDiffRatio']
				: '';
		return {
			type: 'response too similar',
			cacheKey,
			modelInput: { selection, doc },
			inputOutputDiffRatio,
		};
	}

	return {
		type: 'error',
		errorInfo: {
			apiName: getAPIName(requestOptions.endpoint),
			failureReason: FAILURE_REASON.API_FAIL,
			statusCode: data.error?.statusCode ?? 9002,
		},
	};
}

function handleLineGeneratedContent({
	streamContentProcessor,
	partial,
	data,
}: StreamHandlerProps): ResponseObject {
	partial.content += data['generatedContent'];
	if (
		parsedHasExpectedKey('meta', data) &&
		parsedHasExpectedKey('inputOutputDiffRatio', data.meta)
	) {
		partial.meta = {
			inputOutputDiffRatio: data['meta']['inputOutputDiffRatio'],
			loadingStatus: '',
		};
	}

	return {
		type: 'stream',
		loadingStatus: partial.meta.loadingStatus,
		markdown: partial.content,
		...streamContentProcessor?.(partial.content),
	};
}

function handleLineConvoAITrace({ partial, data }: StreamHandlerProps): void {
	const loadingStatus =
		parsedHasExpectedKey('message', data) && parsedHasExpectedKey('message_template', data.message)
			? data.message.message_template
			: '';
	partial.meta = { inputOutputDiffRatio: '', loadingStatus };
}

function handleLineConvoAIAnswerPart({ partial, data, adfStreamer }: StreamHandlerProps): void {
	if (adfStreamer) {
		if (canProcessADFStreaming) {
			adfStreamer.processChunk(data.message.content);
			canProcessADFStreaming = false;
		} else {
			adfStreamer.appendChunk(data.message.content);
		}
		partial.adfResponse = adfStreamer.getLastDocWithoutLastLeafNode();
		// When adf streaming is enabled, don't need to keep the content in markdown
		partial.content = '';
		partial.meta = { inputOutputDiffRatio: '', loadingStatus: '' };
	} else {
		partial.content += data.message.content;
		partial.meta = { inputOutputDiffRatio: '', loadingStatus: '' };
	}
}

function processADFStreamingFinalResponse({
	content,
	adfStreamer,
	partial,
	context,
}: {
	adfStreamer: ADFStreamer;
	content: string;
	context: AIExperienceMachineContextForRequest;
	partial: LinePartial;
}): void {
	// Reset the adfStreamer to ensure it starts fresh for the final response to avoid any potential issues
	adfStreamer.reset();
	adfStreamer.setAnalyticsEventMetaData({ isFinalResponse: true });
	const rawAdfResponse = adfStreamer.processChunk(content).getLastDoc();

	partial.adfResponse = partial.initialSelectionContext
		? correctADFCapitalization(rawAdfResponse, partial.initialSelectionContext)
		: rawAdfResponse;

	const inputSlice = getInputSlice(context);
	adfStreamer.setInputDocumentTree(inputSlice);
	// To fire analytics event for final response, need to end the streaming session
	adfStreamer.endStreamingSession({ hasProcessedFinalResponse: true });
	// When adf streaming is enabled, don't need to keep the content in markdown
	partial.content = '';
}

function handleLineConvoAIFinalResponse({
	requestOptions,
	cacheKey,
	data,
	partial,
	adfStreamer,
	context,
}: StreamHandlerProps): ResponseObject | undefined {
	const { message } = data;
	const { selection, doc } = requestOptions;
	function handleResponseTooSimilar(inputOutputDiffRatio: string): ResponseObject {
		if (adfStreamer) {
			processADFStreamingFinalResponse({
				content: message.content,
				adfStreamer,
				partial,
				context,
			});
		}

		return {
			type: 'response too similar',
			cacheKey,
			modelInput: { selection, doc },
			inputOutputDiffRatio,
			adfResponse: partial.adfResponse,
		};
	}
	// if response too similar, early break/return
	if (
		parsedHasExpectedKey('message_metadata', message.message) &&
		parsedHasExpectedKey('response_too_similar', message.message.message_metadata) &&
		message.message.message_metadata.response_too_similar
	) {
		return handleResponseTooSimilar(message.message?.message_metadata?.diff_ratio || '');
	} else if (parsedHasExpectedKey('response_too_similar', data) && data.response_too_similar) {
		// Convo AI has a different structure compared to the Assistance Service
		// If 'response_too_similar' is a direct property of 'data', it indicates a Convo AI response
		return handleResponseTooSimilar(
			parsedHasExpectedKey('similarity_ratio', data) ? `${data.similarity_ratio}` : '',
		);
	}

	if (parsedHasExpectedKey('content', message)) {
		if (adfStreamer) {
			processADFStreamingFinalResponse({
				content: message.content,
				adfStreamer,
				partial,
				context,
			});
		} else {
			// Handle New Convo AI response structure
			partial.content = message.content;
		}
		if (parsedHasExpectedKey('actions', message) && Array.isArray(message.actions)) {
			partial.rovoActions = message.actions || [];
		}
		partial.meta = {
			inputOutputDiffRatio: parsedHasExpectedKey('similarity_ratio', data)
				? `${data.similarity_ratio}`
				: '',
			loadingStatus: '',
		};
		return;
	}

	// Else handle Assistance Service response structure
	if (adfStreamer) {
		processADFStreamingFinalResponse({
			content: message.content,
			adfStreamer,
			partial,
			context,
		});
	} else {
		partial.content = message.message.content;
	}
	partial.rovoActions = message.message.actions || [];
	partial.meta = {
		inputOutputDiffRatio: message.message?.message_metadata?.diff_ratio || '',
		loadingStatus: '',
	};
}

function handleLineConvoAIError({
	requestOptions,
	cacheKey,
	data,
}: StreamHandlerProps): ResponseObject {
	let statusCode;
	let guard = 'UNHANDLED_ERROR';
	let error;
	let retryAfter: number | undefined;

	if (parsedHasExpectedKey('message', data)) {
		// Get status code
		if (
			parsedHasExpectedKey('status_code', data.message) &&
			typeof data.message.status_code === 'number'
		) {
			statusCode = data.message.status_code;
		}

		// Get reason
		if (parsedHasExpectedKey('message_template', data.message)) {
			guard = data.message.message_template;
		}

		// Get error
		if (parsedHasExpectedKey('content', data.message)) {
			error = data.message.content;
		}

		// Get retryAfter
		if (
			parsedHasExpectedKey('headers', data) &&
			parsedHasExpectedKey('Retry-After', data.headers)
		) {
			retryAfter = Number(data.headers['Retry-After']);
		}
	}

	let failureReason = FAILURE_REASON.API_FAIL;

	if (['RATE_LIMIT', 'OPENAI_RATE_LIMIT_USER_ABUSE'].includes(guard) && retryAfter) {
		failureReason = FAILURE_REASON.RATE_LIMITED;
	} else {
		retryAfter = undefined;
	}

	if (guard === 'HIPAA_CONTENT_DETECTED') {
		failureReason = FAILURE_REASON.HIPAA_CONTENT;
	}

	if (guard === 'EXCEEDING_CONTEXT_LENGTH_ERROR') {
		failureReason = FAILURE_REASON.TOKEN_LIMIT;
	}

	const apiName = getAPIName(requestOptions.endpoint);

	if (guard === 'ACCEPTABLE_USE_VIOLATIONS') {
		return {
			type: 'error',
			cacheKey,
			modelInput: { selection: requestOptions.selection, doc: requestOptions.doc },
			errorInfo: {
				failureReason: FAILURE_REASON.AUP_VIOLATION,
				statusCode,
				errorKey: guard,
				apiName,
				errorContent: sanitizeErrorContent(error),
			},
		};
	}

	if (retryAfter) {
		return {
			type: 'error',
			retryAfter,
			errorInfo: {
				apiName,
				failureReason,
				statusCode,
				errorKey: guard,
				errorContent: sanitizeErrorContent(error),
			},
		};
	}

	return {
		type: 'error',
		errorInfo: {
			apiName,
			failureReason,
			statusCode,
			errorKey: guard,
			errorContent: sanitizeErrorContent(error),
		},
	};
}

function handleLineConvoAI(props: StreamHandlerProps): ResponseObject {
	const { data, adfStreamer, partial } = props;
	if (parsedHasExpectedKey('type', data)) {
		switch (data.type) {
			case 'TRACE':
				handleLineConvoAITrace(props);
				break;
			case 'ANSWER_PART':
				handleLineConvoAIAnswerPart(props);
				break;
			case 'FINAL_RESPONSE':
				/**
				 * When final response is received, all chunks received so far are processed.
				 * So, we need to end the streaming session to fire analytics events.
				 *
				 * Final response has all chunks combined, so adfStreamer is reset before
				 * 	processing final response.
				 * After processing final response, we can end the streaming session with
				 * 	isFinalResponse set to true in analytics event meta data.
				 * And will call endStreamingSession() to fire analytics events.
				 */
				stopADFStreamingProcessTimer();
				const inputSlice = getInputSlice(props.context);
				adfStreamer?.setInputDocumentTree(inputSlice);
				adfStreamer?.endStreamingSession();
				const error = handleLineConvoAIFinalResponse(props);
				if (error) {
					return error;
				}
				break;
			case 'ERROR':
				stopADFStreamingProcessTimer();
				return handleLineConvoAIError(props);
		}
	}
	// If there are any unexpected loading responses, we should return the current data
	return adfStreamer
		? {
				type: 'stream',
				loadingStatus: partial.meta.loadingStatus,
				markdown: partial.content,
				adfResponse: partial.adfResponse,
			}
		: { type: 'stream', loadingStatus: partial.meta.loadingStatus, markdown: partial.content };
}

function handleLine(props: StreamHandlerProps): ResponseObject {
	if (parsedHasExpectedKey('generatedContent', props.data)) {
		return handleLineGeneratedContent(props);
	} else if (props.requestOptions.isConvoAI) {
		return handleLineConvoAI(props);
	} else {
		// if not expected response from the API the we have an error
		return {
			type: 'error',
			errorInfo: {
				failureReason: FAILURE_REASON.API_FAIL,
				apiName: getAPIName(props.requestOptions.endpoint),
				statusCode: 9003,
			},
		};
	}
}

function handleLoaded({
	requestOptions,
	cacheKey,
	selectionType,
	streamContentProcessor,
	isComplete,
	partial,
	schema,
	isAdfPrompt,
}: Omit<StreamHandlerProps, 'data'>): ResponseObject {
	const rovoActions = partial.rovoActions || [];

	let markdown;
	let adfResponse;

	if (isAdfPrompt) {
		markdown = parseAdfToMarkdown(
			partial.adfResponse || { type: 'doc', version: 1, content: [] },
			schema,
		);
		adfResponse = partial.adfResponse;
	} else {
		markdown = partial.content;
	}
	let processedResponse: ReturnType<StreamContentProcessor> = { markdown };

	if (rovoActions.length > 0) {
		const idealSuggestion = selectionType === 'range' ? 'replace' : 'insert';
		const action = rovoActions.find((x) => x.data.suggestion === idealSuggestion) ?? rovoActions[0];

		// There is a scenario where we are in selectionType === 'empty'
		// but AI returns replace. In this case, we override the replace
		// suggestion with insert.
		if (selectionType === 'empty' && action.data.suggestion === 'replace') {
			// This should also update the value by reference in rovoActions
			action.data.suggestion = 'insert';
		}
		// The opposite can happen too
		else if (selectionType === 'range' && action.data.suggestion === 'insert') {
			// This should also update the value by reference in rovoActions
			action.data.suggestion = 'replace';
		}

		markdown += `\n\n---\n\n${action.data.content}`;
	} else if (streamContentProcessor) {
		processedResponse = streamContentProcessor(partial.content);
	}

	if (isComplete) {
		const isCompleted = isComplete();
		if (!isCompleted) {
			return { type: 'stream', ...processedResponse, loadingStatus: partial.meta?.loadingStatus };
		}
	}

	return {
		type: 'complete',
		...processedResponse,
		...(adfResponse && { adfResponse }),
		cacheKey,
		modelInput: { selection: requestOptions.selection, doc: requestOptions.doc },
		inputOutputDiffRatio: partial.meta?.inputOutputDiffRatio,
		rovoActions,
	};
}

/**
 * Extracts initial selection context for capitalization correction
 */
function getInitialSelectionContext(context: AIExperienceMachineContextForRequest) {
	const doc = context.editorView.state.doc;
	const positionToTextIndex = (proseMirrorPos: number): number => {
		return doc.textBetween(0, Math.min(proseMirrorPos, doc.content.size), '\n', ' ').length;
	};

	const fullDocumentText = doc.textBetween(0, doc.content.size, '\n', ' ');
	const originalText = doc.textBetween(
		Math.max(0, context.positions[0]),
		Math.min(context.positions[1], doc.content.size),
		'\n',
		' ',
	);

	const selectionStart = positionToTextIndex(context.positions[0]);
	const selectionEnd = positionToTextIndex(context.positions[1]);

	return {
		fullDocumentText,
		originalText,
		selectionEnd,
		selectionStart,
	};
}

interface LinePartial {
	/**
	 * When adf streaming is enabled, every stream content will be processed
	 *  by ADFStreamer and final document will be set in adfResponse.
	 */
	adfResponse?: DocNode;
	/**
	 * When all prompts are migrated to use ADF streaming,
	 * 	content will be removed.
	 */
	content: string;
	/**
	 * Initial selection context captured at start of streaming for capitalization correction
	 */
	initialSelectionContext?: {
		fullDocumentText: string;
		originalText: string;
		selectionEnd: number;
		selectionStart: number;
	};
	meta: EditorPluginAIPromptResponseMeta;
	rovoActions: ConvoAIResponseRovoAction[];
	/**
	 * No need to have "type" here, it is not used anywhere.
	 * Will remove it when all prompts are migrated to use ADF streaming.
	 */
	type: 'markdown';
}

async function* streamingBody({
	requestOptions,
	cacheKey,
	context,
	selectionType,
	streamContentProcessor,
	isComplete,
	response,
	body,
	adfStreamer,
	abortController,
	callback,
}: {
	abortController: AbortController;
	adfStreamer?: ADFStreamer;
	body: ReadableStream<Uint8Array>;
	cacheKey: string;
	callback: Callback;
	context: AIExperienceMachineContextForRequest;
	isComplete?: IsComplete;
	requestOptions: SteamRequestOptions;
	response: Response;
	selectionType: SelectionType;
	streamContentProcessor?: StreamContentProcessor;
}): AsyncGenerator<ResponseObject> {
	try {
		const reader = body.getReader();
		const decoder = new TextDecoder('utf-8');
		let buffer = '';
		let done = false;

		const adfPrompt = context?.configItem?.adfPrompt;
		const initialSelectionContext = adfPrompt ? getInitialSelectionContext(context) : undefined;

		const partial: LinePartial = {
			type: 'markdown',
			content: '',
			meta: { inputOutputDiffRatio: '' },
			rovoActions: [],
			initialSelectionContext,
		};

		while (!done) {
			const { value, done: doneReading } = await reader.read();
			done = doneReading;
			const chunkValue = decoder.decode(value);
			buffer = buffer + chunkValue;
			// Split the buffer by line breaks
			// eslint-disable-next-line @atlassian/perf-linting/no-expensive-split-replace -- Ignored via go/ees017 (to be fixed)
			const lines = buffer.split('\n');
			// Process all complete lines, except for the last one (which might be incomplete)
			while (lines.length > 1) {
				// Ignored via go/ees005
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const line = lines.shift()!;

				let data;
				try {
					data = JSON.parse(line);
				} catch (jsonError) {
					// Handle invalid JSON - revert content and show error with retry option
					return yield {
						type: 'error',
						errorInfo: {
							apiName: getAPIName(requestOptions.endpoint),
							failureReason: FAILURE_REASON.INVALID_JSON,
							statusCode: response.status,
							errorContent: `Received invalid json: ${
								jsonError instanceof Error ? jsonError.message : String(jsonError)
							}`,
						},
					};
				}

				if (data.generatedContent === null) {
					return yield handleLineNoGeneratedContent({
						requestOptions,
						cacheKey,
						context,
						streamContentProcessor,
						partial,
						data,
						adfStreamer,
						selectionType,
						schema: context.editorView?.state?.schema,
						isAdfPrompt: !!adfPrompt,
					});
				}

				yield handleLine({
					requestOptions,
					cacheKey,
					context,
					streamContentProcessor,
					partial,
					data,
					adfStreamer,
					selectionType,
					schema: context.editorView?.state?.schema,
					isAdfPrompt: !!adfPrompt,
				});

				if (
					parsedHasExpectedKey('idMap', data) &&
					typeof data.idMap === 'object' &&
					data.idMap !== null &&
					Object.keys(data.idMap).length > 0
				) {
					callback({ type: 'update idMap', context: { idMap: data.idMap } });
				}
			}
			// Keep the last (potentially incomplete) line in the buffer
			buffer = lines[0];
		}

		return yield handleLoaded({
			requestOptions,
			cacheKey,
			context,
			selectionType,
			streamContentProcessor,
			isComplete,
			partial,
			schema: context.editorView?.state?.schema,
			isAdfPrompt: !!adfPrompt,
		});
	} catch (parsingError) {
		/**
		 * Retry Manager will abort request if there are ADF parsing is failing
		 * 	for specified duration and retry attempt has not reached max retries.
		 *
		 * It will abort previous request which will throw AbortError.
		 *
		 * isThisAutomaticRetry will be true if this is retry attempt.
		 */
		if (
			!(
				abortController.signal.aborted &&
				parsingError instanceof Error &&
				parsingError.name === 'AbortError'
			)
		) {
			return yield {
				type: 'error',
				errorInfo: {
					apiName: getAPIName(requestOptions.endpoint),
					failureReason: FAILURE_REASON.API_FAIL,
					statusCode: response.status,
					errorContent: sanitizeErrorContent(parsingError),
				},
			};
		}
	}
}

function makeEndpoint({ endpoint, channelId }: { channelId?: string; endpoint: string }) {
	let actualEndpoint = endpoint;

	// Using a stateful endpoint
	if (channelId) {
		actualEndpoint = `${endpoint}/${channelId}/message/stream`;
	}

	return actualEndpoint;
}

function makeHeaders({
	product,
	experienceId,
}: {
	experienceId: string;
	product: EditorPluginAIProvider['product'];
}): Record<string, string> {
	return {
		'Content-Type': 'application/json;charset=UTF-8',
		'X-Experience-Id': experienceId,
		...getXChannelIdHeader(experienceId),
		...getXProductHeader(product),
	};
}

export interface StartStreamingOptions {
	abortController: AbortController;
	aiInteractionID?: string;
	cacheKey: string;
	callback: Callback;
	channelId?: string | undefined;
	context: AIExperienceMachineContextForRequest;
	fireAIAnalyticsEvent?: FireAIAnalyticsEventCallback;
	getFetchCustomHeaders:
		| EditorPluginAIProvider['getFetchCustomHeaders']
		| (() => Record<string, string>);
	isComplete?: IsComplete;
	product: EditorPluginAIProvider['product'];
	requestOptions: RequestOptions;
	selectionType: SelectionType;
	streamContentProcessor?: StreamContentProcessor;
	streamFlowPacer: StreamFlowPacer | null;
}

/**
 * Start streaming AI response with support for reverting inappropriate API capitalization changes.
 * experienceId and channelId are for convoai/assistance-service
 */
export async function startStreaming(options: StartStreamingOptions): Promise<void> {
	const {
		abortController,
		cacheKey,
		selectionType,
		product,
		getFetchCustomHeaders,
		channelId,
		callback,
		streamFlowPacer,
		streamContentProcessor,
		isComplete,
		requestOptions,
		context,
		fireAIAnalyticsEvent,
		aiInteractionID,
	} = options;

	if ('response' in requestOptions) {
		callback(requestOptions.response);
		return;
	}

	if ('error' in requestOptions) {
		// eslint-disable-next-line no-console
		console.error('Editor AI: startStreamingResponse()', requestOptions.error);
		callback({
			type: 'error',
			errorInfo: {
				apiName: getAPIName(requestOptions.endpoint),
				failureReason: FAILURE_REASON.API_FAIL,
				statusCode: 400,
				errorContent: requestOptions.error,
			},
		});
		return;
	}

	const { experienceId, endpoint, payload } = requestOptions;
	// After early return checks above, requestOptions must be the payload-containing variant
	const typedRequestOptions = requestOptions as Extract<RequestOptions, { payload: string }>;

	let streamCallback = callback;
	let adfStreamingRetryManager: ADFStreamingRetryManager | null = null;
	function createNewADFStreamer() {
		return createADFStreamer({
			state: context.editorView.state,
			/*
			 * Remove use of null coalescing operator when last
			 *  experiment for adf streaming is removed.
			 */
			fireAIAnalyticsEvent: fireAIAnalyticsEvent ?? (() => {}),
			experienceName: context.configItem.key,
			featureId: context.configItem.featureId,
			aiSessionId: context.analyticsContext.aiSessionId,
			aiInteractionID,
			selectionType,
			contentStatistics: typedRequestOptions.contentStatistics,
			selectionCategory: getSelectionCategory(context.editorView, context.positions),
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			streamId: uuidv4(),
			retryAttemptNumber: adfStreamingRetryManager?.getRetryCount() ?? 0,
			traceId: context.traceIds?.[0] ?? undefined,
		});
	}

	const isAdfPrompt = context?.configItem?.adfPrompt;
	let adfStreamer = isAdfPrompt ? createNewADFStreamer() : undefined;

	async function fetcher(
		abortController: AbortController,
		retryManager?: ADFStreamingRetryManager,
	) {
		try {
			if (streamFlowPacer && (isAdfPrompt || context.aiProvider.isInDocumentStreamingEnabled)) {
				// when we retry after an error, we need to reset
				streamFlowPacer.reset();
				streamFlowPacer.setEmit(callback);
				/**
				 * If AIFC is disabled, then AI response will be shown in LoadingScreen.
				 * GPT models returns very very small chunks (often times 2-3 chars).
				 * This causes too many re-renders of Renderer component in LoadingScreen,
				 * 	which affects performance.
				 *
				 * So using streamFlowPacer to throttle the stream and process chunks in batches.
				 * Currently, it re-render LoadingScreen 4 times per second.
				 */
				if (isAdfPrompt && !context.aiProvider.isInDocumentStreamingEnabled) {
					streamFlowPacer.setTickMs(ADF_STREAMING_PROCESS_INTERVAL);
					streamFlowPacer.setCharsPerTick(ADF_STREAMING_PROCESS_INITIAL_CHARS_PER_TICK);
					streamFlowPacer.setMaxCharsPerTick(ADF_STREAMING_PROCESS_MAX_CHARS_PER_TICK);
				}
				streamFlowPacer.start();
				streamCallback = streamFlowPacer.push;
			}

			if (isAdfPrompt) {
				startADFStreamingProcessTimer();
			}

			const actualEndpoint = makeEndpoint({ endpoint, channelId });
			const fetchInit: RequestInit = {
				method: 'POST',
				headers: makeHeaders({ product, experienceId }),
				body: payload,
				credentials: 'include',
				signal: abortController.signal,
				mode: 'cors',
			};
			const fetchCustomHeaders = getFetchCustomHeaders?.(endpoint, fetchInit) || {};
			const response = await fetch(actualEndpoint, {
				...fetchInit,
				headers: { ...fetchInit.headers, ...fetchCustomHeaders },
			});

			const traceId = response.headers.get('X-Trace-Id') || response.headers.get('Atl-Traceid');
			if (traceId) {
				callback({ type: 'update traceIds', context: { traceIds: [traceId] } });
			}

			if (!response.ok) {
				return callback(
					await handleResponseNotOK({ requestOptions: typedRequestOptions, cacheKey, response }),
				);
			}

			if (!response.body) {
				callback(handleNoResponseBody({ requestOptions: typedRequestOptions, response }));
				return;
			}

			if (context?.configItem?.handleFetchResponse) {
				const data = await response.json();
				context.configItem.handleFetchResponse(data, requestOptions, callback, cacheKey);
				return;
			}

			const streaming = streamingBody({
				requestOptions: typedRequestOptions,
				cacheKey,
				selectionType,
				streamContentProcessor,
				isComplete,
				response,
				body: response.body,
				context,
				adfStreamer,
				abortController,
				callback,
			});

			for await (const item of streaming) {
				streamCallback(item);
				// If we have a response other than stream (which means there is an error), we should stop the streaming
				if (item.type !== 'stream') {
					/**
					 * When streaming ends, check if ADFStreamer is in error state.
					 * Retry if possible if in error state.
					 */
					if (retryManager) {
						if (adfStreamer?.isInErrorState()) {
							if (retryManager.hasMaxRetryReached()) {
								callback({
									type: 'error',
									errorInfo: {
										apiName: getAPIName(endpoint),
										failureReason: FAILURE_REASON.ADF_STREAMING_ERROR,
									},
								});
							} else {
								retryManager.retryOnStreamingEnd();
							}
						} else {
							retryManager.reset();
						}
					}
					return;
				}
			}
		} catch (error) {
			adfStreamingRetryManager?.reset();
			stopADFStreamingProcessTimer();
			streamCallback(handleRequestError(error, endpoint));
		}
	}

	/**
	 * When AIFC is enabled, it will stream directly into document.
	 * Currently, automatic retry will only happen when in document streaming is disabled.
	 */
	if (isAdfPrompt && !context.aiProvider.isInDocumentStreamingEnabled) {
		adfStreamingRetryManager = new ADFStreamingRetryManager({
			maxRetries: ADF_STREAMING_RETRY_MAX_RETRIES,
			delayMs: ADF_STREAMING_RETRY_DELAY_MS,
			beforeRetryCallback: () => {
				callback({ type: 'retry on adf stream errors', adfResponse: getEmptyADF() });
				adfStreamer?.endStreamingSession();
				adfStreamer?.reset();
				adfStreamer = createNewADFStreamer();
			},
			fetcher,
			errorChecker: () => {
				return adfStreamer?.isInErrorState() ?? false;
			},
		});

		adfStreamingRetryManager?.startFetchingWithRetry();
	} else {
		await fetcher(abortController);
	}
}
